import { useEffect } from 'react';
import { useGameState } from '../../net/GameProvider.js';
import { useI18n } from '../../i18n.js';
import { AuthForms, type AuthViewMode } from '../profile/AuthForms.js';
import type { InfoPageType } from '../InfoPages.js';
import { useSafeBackdropDismiss } from '../../utils/modalUtils.js';

export interface AuthModalProps {
  onClose: () => void;
  onSuccess?: () => void;
  initialView?: AuthViewMode;
  onOpenInfoPage?: (page: InfoPageType) => void;
}

export function AuthModal({
  onClose,
  onSuccess,
  initialView = 'signup',
  onOpenInfoPage,
}: AuthModalProps) {
  const { state } = useGameState();
  const { lang } = useI18n();
  const isMn = lang === 'mn';
  const backdropHandlers = useSafeBackdropDismiss(onClose);

  // Automatically close when profile is authenticated
  useEffect(() => {
    if (state.profile) {
      onClose();
      onSuccess?.();
    }
  }, [state.profile, onClose, onSuccess]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={isMn ? 'Нэвтрэх болон Бүртгүүлэх' : 'Sign In and Register'}
      className="modal-overlay"
      {...backdropHandlers}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.78)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        zIndex: 250,
        padding: '24px 16px',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        overscrollBehaviorY: 'contain',
      }}
    >
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'linear-gradient(160deg, #0b2919 0%, #071a10 100%)',
          border: '1px solid rgba(251, 191, 36, 0.45)',
          borderRadius: 16,
          width: '100%',
          maxWidth: 480,
          margin: 'auto 0',
          padding: 24,
          boxShadow: '0 20px 50px rgba(0,0,0,0.75)',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: '1.25em', color: '#fde68a' }}>
            {isMn ? 'Нэвтрэх / Бүртгүүлэх' : 'Sign In / Register'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.12)',
              color: '#fff',
              padding: '4px 12px',
              borderRadius: 8,
              cursor: 'pointer',
            }}
          >
            ✕
          </button>
        </div>

        {/* Forms */}
        <AuthForms initialView={initialView} onSuccess={onSuccess} />

        {/* Info Links */}
        {onOpenInfoPage && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '6px 14px',
              paddingTop: 10,
              borderTop: '1px solid rgba(255,255,255,0.12)',
              fontSize: '0.78em',
            }}
          >
            <a
              href="/subscription"
              onClick={(e) => {
                e.preventDefault();
                onClose();
                onOpenInfoPage('subscription');
              }}
              style={{ color: '#fde68a', textDecoration: 'none', fontWeight: 700 }}
            >
              👑 {isMn ? 'VIP Гишүүнчлэлийн мэдээлэл' : 'VIP Benefits & Subscription'}
            </a>
            <a
              href="/terms"
              onClick={(e) => {
                e.preventDefault();
                onClose();
                onOpenInfoPage('terms');
              }}
              style={{ color: 'rgba(255,255,255,0.75)', textDecoration: 'none' }}
            >
              {isMn ? 'Үйлчилгээний нөхцөл' : 'Terms of Service'}
            </a>
            <a
              href="/privacy"
              onClick={(e) => {
                e.preventDefault();
                onClose();
                onOpenInfoPage('privacy');
              }}
              style={{ color: 'rgba(255,255,255,0.75)', textDecoration: 'none' }}
            >
              {isMn ? 'Нууцлалын бодлого' : 'Privacy Policy'}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
